import { z } from "zod";

/**
 * Common error schema for RPC responses
 */
export const ErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Standard success response wrapper
 */
export const SuccessResponseSchema = z.object({
  success: z.literal(true),
  data: z.unknown(),
});

/**
 * Standard error response wrapper
 */
export const ErrorResponseSchema = z.object({
  success: z.literal(false),
  error: ErrorSchema,
});

/**
 * Standard RPC response that can be either success or error
 */
export const RpcResponseSchema = z.union([
  SuccessResponseSchema,
  ErrorResponseSchema,
]);

/**
 * Pagination input schema
 */
export const PaginationInputSchema = z.object({
  page: z.int().min(1).prefault(1),
  limit: z.int().min(1).max(100).prefault(10),
});

/**
 * Offset paging input: a page of up to `limit` rows (default 50), starting `offset` rows in.
 */
export const OffsetPageInputSchema = z.object({
  limit: z.number().int().min(1).max(200).default(50),
  offset: z.number().int().min(0).default(0),
});

/**
 * Opt-in offset paging, as a shape to spread into a list input: omit `limit`
 * to get every matching row in one response.
 */
export const OptionalOffsetPageShape = {
  limit: z.number().int().min(1).max(500).optional(),
  offset: z.number().int().min(0).optional(),
};

/**
 * Pagination info for responses
 */
export const PaginationInfoSchema = z.object({
  page: z.int(),
  limit: z.int(),
  total: z.int(),
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
});

export type ErrorType = z.infer<typeof ErrorSchema>;
export type SuccessResponse<T = unknown> = z.infer<
  typeof SuccessResponseSchema
> & { data: T };
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
export type RpcResponse<T = unknown> = SuccessResponse<T> | ErrorResponse;
export type PaginationInput = z.infer<typeof PaginationInputSchema>;
export type PaginationInfo = z.infer<typeof PaginationInfoSchema>;

/**
 * Lenient enum schema for output validation.
 * Normalizes case and falls back to null for unrecognized values,
 * so one bad DB row doesn't fail the entire list response.
 *
 * Use in output schemas where data comes from the DB and may have
 * been written by external sources (OCR, integrations, migrations).
 */
export function lenientEnum(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: z.ZodEnum<any>,
) {
  return z
    .string()
    .transform((v) => v.toLowerCase())
    .pipe(schema)
    .nullable()
    .catch(() => null);
}

/**
 * A file download's raw bytes, for procedures that stream a response body
 * instead of resolving to a JSON value.
 *
 * oRPC passes a body straight through only when it is an instance of the global
 * `ReadableStream`; anything else is JSON-serialized, which would corrupt the
 * download. Handlers returning this set `Content-Type` / `Content-Disposition`
 * themselves, and clients trigger them as a browser navigation to the RPC GET
 * URL (see the frontend's `startRpcDownload`) rather than through the typed
 * client — a `fetch(...).blob()` would buffer the whole file in tab memory and
 * show no progress until it finished.
 */
export const FileStreamSchema = z.custom<ReadableStream<Uint8Array>>(
  (value) => value instanceof ReadableStream,
  { message: "Expected a ReadableStream of file bytes" },
);

export const SortDirectionSchema = z.enum(["asc", "desc"]);
export type SortDirection = z.infer<typeof SortDirectionSchema>;

/**
 * Sort levels a list may add after its primary `sortBy`/`sortDirection`, so a
 * table sorts by at most `1 + MAX_THEN_SORT_LEVELS` columns.
 */
export const MAX_THEN_SORT_LEVELS = 2;

/**
 * A list input's secondary sort levels, applied in order after the primary
 * `sortBy`/`sortDirection` breaks its ties. Kept beside the primary pair rather
 * than replacing it, so existing links, saved views and callers stay valid.
 */
export function thenSortSchema<TSortBy extends z.ZodType<string>>(
  sortBy: TSortBy,
) {
  return z
    .array(z.object({ sortBy, sortDirection: SortDirectionSchema }))
    .max(MAX_THEN_SORT_LEVELS);
}

/**
 * The caller's visible table layout for a "download selected rows as PDF"
 * endpoint: column ids in on-screen order, their display labels, and relative
 * widths, so the document matches the table the user configured.
 */
export function tablePdfColumnsSchema<TColumnId extends z.ZodType<string>>(
  id: TColumnId,
) {
  return z
    .array(
      z.object({
        id,
        /** Display label as rendered in the UI (localized client-side). */
        label: z.string().min(1).max(80),
        /** Relative width weight; the on-screen pixel width works. */
        width: z.number().positive().max(10_000),
      }),
    )
    .min(1)
    .max(20);
}

/** A generated PDF: oRPC is JSON-based, so the bytes travel base64-encoded. */
export const PdfDownloadOutputSchema = z.object({
  pdf: z.string(),
  filename: z.string(),
});

/**
 * A server-sorted list's sort input, spread into its input schema: the
 * primary column and direction plus the `thenSort` tie-breakers. Pairs with
 * the backend's `buildListOrder`, which needs SQL for every `sortBy` value.
 */
export function listSortShape<TSortBy extends z.ZodType<string>>(
  sortBy: TSortBy,
) {
  return {
    sortBy: sortBy.optional(),
    sortDirection: SortDirectionSchema.optional(),
    /** Tie-breaking sort levels applied after `sortBy`. */
    thenSort: thenSortSchema(sortBy).optional(),
  };
}
